import { useEssentials } from 'hooks/utils/useEssentials'
import {
    useNddStoreMetrics,
    useHistoricalNddStoreMetrics,
} from 'hooks/ndd/useNddMetrics'
import { Grid } from '@design-systems/canvas-ui'
import { Table } from 'components/table-compat/table-compat'
import { formattedTimeStamp } from 'pages/fulfillment/helpers'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { useState, useMemo, useCallback } from 'react'
import {
    TableData,
    Cell,
    applyConditionalFormatting,
    buildDataTableObject,
    getColorClass,
} from 'utils/data'
import { RouteSortDetail, HandOffDetail } from 'types/ndd'
import { BasicPlaceholder } from 'components/placeholder'
import { ErrorAlert } from 'components/error-fallback'
import useCountdown from 'hooks/utils/useCountdown'
import { GeneralizedComponent } from 'components/generalized-component/generalized-component'
import { Constants } from 'constants/constants'

const TODAY_TABLE_WIDTHS = [80, 80, 80, 80]
const HISTORICAL_TABLE_WIDTHS = [74, 60, 74, 80]

const ROUTE_SORT_HEADINGS = (t: any, k: any): Cell[] => [
    { value: t(k.ndd.byTime) },
    { value: t(k.ndd.completed) },
    { value: t(k.ndd.totalExpected) },
    { value: t(k.ndd.percentOnTime) },
]
const HANDOFF_HEADINGS = (t: any, k: any): Cell[] => [
    { value: '' },
    { value: t(k.ndd.completed) },
    { value: t(k.ndd.totalExpected) },
    { value: t(k.ndd.accuracy) },
]

const buildPercentCell = (display: string, isPositive: boolean) =>
    applyConditionalFormatting(
        display,
        isPositive ? 'hc-clr-success' : 'hc-clr-error',
    )

function buildRouteSortTable(
    details: RouteSortDetail[] | undefined,
    t: any,
    k: any,
): TableData {
    return {
        headings: ROUTE_SORT_HEADINGS(t, k),
        rows: Array.isArray(details)
            ? details.map((r) => {
                  const pctDisplay = `${(r.on_time_percentage ?? 0).toFixed(1)}%`
                  const pctCell = buildPercentCell(pctDisplay, r.is_on_time)
                  return [
                      { value: r.sort_due_hour, raw: r.sort_due_hour },
                      {
                          value: String(r.completed ?? 0),
                          raw: String(r.completed ?? 0),
                      },
                      {
                          value: String(r.total_expected ?? 0),
                          raw: String(r.total_expected ?? 0),
                      },
                      { ...pctCell, raw: pctDisplay },
                  ]
              })
            : [],
    }
}
function buildHandoffTable(
    details: HandOffDetail[] | undefined,
    t: any,
    k: any,
): TableData {
    return {
        headings: HANDOFF_HEADINGS(t, k),
        rows: Array.isArray(details)
            ? details.map((d) => {
                  const accuracyPct = d.accuracy_percentage || 0
                  const display = `${Math.round(accuracyPct)}%`
                  const cell = buildPercentCell(display, d.is_accurate)
                  return [
                      {
                          value: d.to_be_handed_off_hour || '',
                          raw: d.to_be_handed_off_hour || '',
                      },
                      {
                          value: String(d.completed ?? 0),
                          raw: String(d.completed ?? 0),
                      },
                      {
                          value: String(d.total_expected ?? 0),
                          raw: String(d.total_expected ?? 0),
                      },
                      { ...cell, raw: display },
                  ]
              })
            : [],
    }
}

function processHistoricalData(
    data: TableData | undefined,
    t: any,
    k: any,
): TableData | undefined {
    if (!data?.headings || !data?.rows) return data

    const headingTranslationMap: { [key: string]: string } = {
        'containers sorted': k.ndd.containersSorted,
        'inf %': k.ndd.infPercent,
        'sorted on time': k.ndd.sortedOnTime,
        'shipped on time': k.ndd.shippedOnTime,
    }

    const targetIndexes: number[] = []
    data.headings.forEach((h, idx) => {
        const normalized = h?.value?.trim().toLowerCase()
        if (normalized && headingTranslationMap[normalized]) {
            targetIndexes.push(idx)
        }
    })

    const formattedRows = data.rows.map((row) =>
        row.map((cell, colIdx) => {
            if (!targetIndexes.includes(colIdx)) return cell
            const colorClass = getColorClass(cell.color)
            if (!colorClass) return cell
            const formatted = applyConditionalFormatting(
                (cell as any).raw || cell.value,
                colorClass,
            )
            return {
                ...cell,
                value: cell.value,
                raw: (cell as any).raw || cell.value,
                className: formatted.className || colorClass,
                icon: cell.icon || formatted.icon,
            }
        }),
    )

    const translatedHeadings = data.headings.map((cell) => {
        const original = cell?.value || ''
        const normalized = original.trim().toLowerCase()
        const translationKey = headingTranslationMap[normalized]
        if (!translationKey) return cell
        return { ...cell, value: t(translationKey) }
    })

    return { ...data, rows: formattedRows, headings: translatedHeadings }
}

export const StoreFulfillmentNddStore = () => {
    const { t, env, session, currentLocation, logout } = useEssentials()
    const { storeFulfillment } = TRANSLATION_KEYS
    const [timePeriodIndex, setTimePeriodIndex] = useState<number>(0)

    const timePeriodOptions = useMemo(
        () => [
            {
                key: Constants.TIME_PERIOD_KEYS.TODAY,
                label: t(storeFulfillment.timePeriods.today),
            },
            {
                key: Constants.TIME_PERIOD_KEYS.YESTERDAY,
                label: t(storeFulfillment.timePeriods.yesterday),
            },
            {
                key: Constants.TIME_PERIOD_KEYS.WEEK_TO_DAY,
                label: t(storeFulfillment.timePeriods.weekToDate),
            },
            {
                key: Constants.TIME_PERIOD_KEYS.LAST_WEEK,
                label: t(storeFulfillment.timePeriods.lastWeek),
            },
            {
                key: Constants.TIME_PERIOD_KEYS.MONTH_TO_DAY,
                label: t(storeFulfillment.timePeriods.monthToDate),
            },
            {
                key: Constants.TIME_PERIOD_KEYS.YEAR_TO_DAY,
                label: t(storeFulfillment.timePeriods.yearToDate),
            },
        ],
        [
            t,
            storeFulfillment.timePeriods.today,
            storeFulfillment.timePeriods.yesterday,
            storeFulfillment.timePeriods.weekToDate,
            storeFulfillment.timePeriods.lastWeek,
            storeFulfillment.timePeriods.monthToDate,
            storeFulfillment.timePeriods.yearToDate,
        ],
    )

    const timePeriods = useMemo(
        () => timePeriodOptions.map((p) => p.label),
        [timePeriodOptions],
    )

    const activeApiPeriodKey = timePeriodOptions[timePeriodIndex].key

    const {
        data: todayData,
        isLoading: isLoadingToday,
        isError: isErrorToday,
        error: errorToday,
        refetch: todayRefetch,
    } = useNddStoreMetrics({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
    })
    const {
        data: historicalData,
        isLoading: isLoadingHistorical,
        isError: isErrorHistorical,
        error: errorHistorical,
        refetch: historicalRefetch,
    } = useHistoricalNddStoreMetrics({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
        timeDimension: activeApiPeriodKey,
    })

    const routeSortTable = useMemo(() => {
        if (timePeriodIndex !== 0) {
            // Avoid building when not needed; return empty structure with headings for stability
            return {
                headings: ROUTE_SORT_HEADINGS(t, storeFulfillment),
                rows: [],
            }
        }
        return buildRouteSortTable(
            todayData?.route_sort_details,
            t,
            storeFulfillment,
        )
    }, [timePeriodIndex, todayData?.route_sort_details, t, storeFulfillment])
    const handoffTable = useMemo(() => {
        if (timePeriodIndex !== 0) {
            return { headings: HANDOFF_HEADINGS(t, storeFulfillment), rows: [] }
        }
        return buildHandoffTable(
            todayData?.hand_off_details,
            t,
            storeFulfillment,
        )
    }, [timePeriodIndex, todayData?.hand_off_details, t, storeFulfillment])
    const formattedHistoricalData: TableData | undefined = useMemo(() => {
        if (timePeriodIndex === 0) return undefined
        return processHistoricalData(
            historicalData as TableData | undefined,
            t,
            storeFulfillment,
        )
    }, [timePeriodIndex, historicalData, t, storeFulfillment])

    // Status flags
    const hasTodayData =
        routeSortTable.rows.length > 0 || handoffTable.rows.length > 0
    const hasHistoricalData = !!formattedHistoricalData?.rows?.length
    const isLoadingSelected =
        timePeriodIndex === 0 ? isLoadingToday : isLoadingHistorical
    const selectedError = timePeriodIndex === 0 ? errorToday : errorHistorical
    const selectedIsError =
        timePeriodIndex === 0 ? isErrorToday : isErrorHistorical

    const count = useCountdown(5, logout, selectedError as Error)

    const nddTimestamp = `${t(storeFulfillment.ndd.asOf)} ${formattedTimeStamp(new Date().toISOString())}`

    const showInitialPlaceholder =
        isLoadingSelected &&
        (timePeriodIndex === 0 ? !hasTodayData : !hasHistoricalData)

    const historicalTabs = hasHistoricalData
        ? {
              [t(storeFulfillment.ndd.routeSort)]:
                  formattedHistoricalData as TableData,
          }
        : {}
    const todayTabs = { __today: routeSortTable }
    const activeTabs = timePeriodIndex === 0 ? todayTabs : historicalTabs
    const isNoDataState =
        !isLoadingSelected && Object.keys(activeTabs).length === 0

    const payload = useMemo(() => {
        return {
            '': timePeriods.map((tp, idx) => ({
                title: tp,
                subtitle: '',
                data: idx === timePeriodIndex ? activeTabs : {},
            })),
        }
    }, [timePeriods, timePeriodIndex, activeTabs])

    const handleTimeChange = useCallback(
        (newIndex: number) => setTimePeriodIndex(newIndex),
        [],
    )

    // retry handler: only retry the failing endpoint
    const handleRetry = useCallback(() => {
        if (timePeriodIndex === 0) {
            if (isErrorToday) {
                void todayRefetch()
            }
        } else {
            if (isErrorHistorical) {
                void historicalRefetch()
            }
        }
    }, [
        timePeriodIndex,
        isErrorToday,
        isErrorHistorical,
        todayRefetch,
        historicalRefetch,
    ])

    if (selectedIsError) {
        return (
            <ErrorAlert
                error={selectedError as Error}
                count={count}
                onRetry={handleRetry}
            />
        )
    }
    if (showInitialPlaceholder) {
        return (
            <div className="placeholder-container" data-testid="placeholder">
                <BasicPlaceholder type="text" rows={1} />
                <BasicPlaceholder type="table" rows={4} columns={4} />
            </div>
        )
    }

    return (
        <Grid.Container data-testid="ndd-store-container">
            <Grid.Item
                size={{
                    xs: 12,
                }}
            >
                <GeneralizedComponent
                    payload={payload}
                    isLoading={isLoadingSelected}
                    scrollableWidth={true}
                    widths={HISTORICAL_TABLE_WIDTHS}
                    expandedWidth={false}
                    columnSize={12}
                    isNoData={isNoDataState}
                    title={timePeriodIndex === 0 ? '\u200B' : ''}
                    subTitle={timePeriodIndex === 0 ? nddTimestamp : ''}
                    kpiName="store fulfillment ndd store"
                    parentHandleClick={(timeIdx) => handleTimeChange(timeIdx)}
                    selectedTimeIndex={timePeriodIndex}
                    hideTable={timePeriodIndex === 0}
                    isTimePeriodSticky={false}
                    isTimePeriodOutsideHeader
                >
                    {timePeriodIndex === 0 && (
                        <Grid.Container
                            className="hc8-mt-3x"
                            data-testid="ndd-today-tables"
                        >
                            <Grid.Item
                                size={{
                                    xs: 12,
                                }}
                            >
                                <p className="hc8-fs-050 hc8-mb-2x">
                                    {t(storeFulfillment.ndd.routeSort)}
                                </p>
                                <Table
                                    data={buildDataTableObject({
                                        currentData: routeSortTable,
                                        widths: TODAY_TABLE_WIDTHS,
                                        pinFirstColumn: true,
                                    })}
                                    scrollableWidth={true}
                                />
                            </Grid.Item>
                            <Grid.Item
                                size={{
                                    xs: 12,
                                }}
                            >
                                <p className="hc8-fs-050 hc8-mb-2x">
                                    {t(storeFulfillment.ndd.handoff)}
                                </p>
                                <Table
                                    data={buildDataTableObject({
                                        currentData: handoffTable,
                                        widths: TODAY_TABLE_WIDTHS,
                                        pinFirstColumn: true,
                                    })}
                                    scrollableWidth={true}
                                />
                            </Grid.Item>
                        </Grid.Container>
                    )}
                </GeneralizedComponent>
            </Grid.Item>
        </Grid.Container>
    )
}
